import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCredits } from "@/contexts/CreditsContext";
import {
  getCreditTransactions,
  getCreditPackages,
  type CreditTransaction,
  type CreditTransactionsPage,
  type CreditPackage,
} from "@/lib/api-client";
import { toast } from "sonner";
import { formatDateTime, formatPrice, formatNumber } from "@/lib/formatters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, CreditCard, ShoppingCart, History } from "lucide-react";

const TX_TYPE_LABEL_KEYS: Record<string, string> = {
  purchase: "txTypes.purchase",
  spend: "txTypes.spend",
  refund: "txTypes.refund",
  bonus: "txTypes.bonus",
  signup_bonus: "txTypes.signupBonus",
  adjustment: "txTypes.adjustment",
};

function formatDate(isoString: string): string {
  return formatDateTime(isoString);
}

const CreditsPage = () => {
  const { t } = useTranslation("credits");
  const { balance, isLoading: balanceLoading } = useCredits();

  function txLabel(tx: CreditTransaction): string {
    if (tx.action_name_de) return tx.action_name_de;
    if (tx.description) return tx.description;
    const key = TX_TYPE_LABEL_KEYS[tx.tx_type];
    return key ? t(key) : tx.tx_type;
  }

  const [txPage, setTxPage] = useState(1);
  const [txData, setTxData] = useState<CreditTransactionsPage | null>(null);
  const [txLoading, setTxLoading] = useState(true);

  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(true);

  // Fetch transactions whenever page changes
  useEffect(() => {
    let cancelled = false;
    setTxLoading(true);
    getCreditTransactions(txPage, 20)
      .then((data) => {
        if (!cancelled) setTxData(data);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("errors.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setTxLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [txPage, t]);

  // Fetch packages once on mount
  useEffect(() => {
    let cancelled = false;
    getCreditPackages()
      .then((data) => {
        if (!cancelled) setPackages(data);
      })
      .catch(() => {
        // Non-critical — silently skip if endpoint not yet live
        if (!cancelled) setPackages([]);
      })
      .finally(() => {
        if (!cancelled) setPackagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalPages = txData
    ? Math.max(1, Math.ceil(txData.total / txData.per_page))
    : 1;

  // Sort packages by sort_order so we can identify the middle one reliably
  const sortedPackages = [...packages].sort(
    (a, b) => a.sort_order - b.sort_order,
  );
  const middleIndex = Math.floor((sortedPackages.length - 1) / 2);

  return (
    <div className="container max-w-4xl py-8 space-y-10">
      {/* ── Section 1: Balance header ── */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <CreditCard className="h-6 w-6 text-primary" />
        </div>
        <div>
          {balanceLoading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <p className="text-4xl font-bold leading-none">
              {formatNumber(balance)} Credits
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            {t("balance.subtitle")}
          </p>
        </div>
      </div>

      {/* ── Section 2: Credit packages ── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <ShoppingCart className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">{t("packages.title")}</h2>
        </div>

        {packagesLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t("packages.loading")}</span>
          </div>
        ) : sortedPackages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("packages.unavailable")}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedPackages.map((pkg, idx) => {
              const isHighlighted = idx === middleIndex;
              return (
                <Card
                  key={pkg.id}
                  className={`relative bg-card border border-border rounded-[10px] transition-shadow hover:shadow-md ${
                    isHighlighted ? "ring-2 ring-primary" : ""
                  }`}
                >
                  {isHighlighted && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      {t("packages.popular")}
                    </Badge>
                  )}
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{pkg.name}</CardTitle>
                    <p className="text-2xl font-bold mt-1">
                      {formatNumber(pkg.credits)}
                      <span className="text-base font-normal text-muted-foreground ml-1">
                        Credits
                      </span>
                    </p>
                    <p className="text-lg font-semibold text-primary">
                      {formatPrice(pkg.price_cents, pkg.currency)}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <Button
                      asChild
                      variant={isHighlighted ? "default" : "outline"}
                      className="w-full"
                    >
                      <a href="mailto:kontakt@firmenfinden.org">
                        {t("packages.contactButton")}
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Section 3: Transaction history ── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">{t("transactions.title")}</h2>
        </div>

        <div className="bg-card border border-border rounded-[10px] overflow-hidden">
          {txLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm p-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("common.loading")}</span>
            </div>
          ) : !txData || txData.items.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">
              {t("transactions.empty")}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        {t("table.date")}
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        {t("table.action")}
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        {t("table.amount")}
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        {t("table.balanceAfter")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {txData.items.map((tx) => (
                      <tr
                        key={tx.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {formatDate(tx.created_at)}
                        </td>
                        <td className="px-4 py-3">{txLabel(tx)}</td>
                        <td
                          className={`px-4 py-3 text-right font-medium tabular-nums whitespace-nowrap ${
                            tx.amount >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {tx.amount >= 0 ? "+" : ""}
                          {formatNumber(tx.amount)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                          {formatNumber(tx.balance_after)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  {t("pagination.pageInfo", {
                    page: txData.page,
                    total: totalPages,
                  })}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                    disabled={txData.page <= 1}
                  >
                    {t("pagination.back")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTxPage((p) => p + 1)}
                    disabled={txData.page >= totalPages}
                  >
                    {t("pagination.next")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditsPage;
